import { ReactNode, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSession } from '@/lib/auth-client';

/**
 * AuthProvider Component (Platform-UI)
 *
 * Wraps the customer-facing Platform-UI to enforce authentication.
 *
 * Behavior:
 * 1. Check session on mount
 * 2. Show loading spinner during auth check
 * 3. Redirect to /login if unauthenticated
 * 4. Render children if authenticated
 * 5. All authenticated users can access FDAIA, AL3Viewer, M-VC
 */

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { data: session, isPending } = useSession();

  // Redirect to login if unauthenticated
  useEffect(() => {
    if (!isPending && !session && location.pathname !== '/login') {
      navigate('/login', { replace: true, state: { from: location } });
    }
  }, [session, isPending, navigate, location]);

  // Loading state
  if (isPending) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center space-y-4">
          <div className="inline-block">
            <svg
              className="animate-spin h-12 w-12 text-blue-600"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          </div>
          <p className="text-sm text-gray-600">
            Verifying authentication...
          </p>
        </div>
      </div>
    );
  }

  // Authenticated - render children
  return <>{children}</>;
}
